import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE } from '../lib/config';
import { CheckCircleIcon, XCircleIcon, EyeIcon } from '@heroicons/react/24/outline';

const AdminIdentityVerificationPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCase, setSelectedCase] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useEffect(() => {
        checkUserRole();
    }, []);

    const checkUserRole = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const res = await fetch(`${API_BASE}/admin/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const u = await res.json();
                setIsSuperAdmin(u.role === 'super_admin');
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        // Admin権限チェック
        if (!user || user.user_type !== 'admin') {
            toast.error('管理者権限が必要です');
            navigate('/');
            return;
        }
        fetchItems();
    }, [user, navigate]);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/admin/identity-verifications?status=all`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Admin-Token': process.env.REACT_APP_ADMIN_TOKEN || 'admin-secret' // Demo fallback
                }
            });
            if (res.ok) {
                const data = await res.json();
                setItems(data);
            } else {
                toast.error('データの取得に失敗しました');
            }
        } catch (e) {
            console.error(e);
            toast.error('エラーが発生しました');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (caseId, status) => {
        if (!window.confirm(status === 'approved' ? '承認しますか？' : '否認しますか？')) return;

        try {
            setProcessing(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/admin/identity-verifications/${caseId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-Admin-Token': process.env.REACT_APP_ADMIN_TOKEN || 'admin-secret'
                },
                body: JSON.stringify({ status })
            });
            if (res.ok) {
                toast.success(status === 'approved' ? '承認しました' : '否認しました');
                fetchItems();
                setShowModal(false);
            } else {
                toast.error('更新に失敗しました');
            }
        } catch (e) {
            console.error(e);
            toast.error('エラーが発生しました');
        } finally {
            setProcessing(false);
        }
    };

    const openModal = (item) => {
        setSelectedCase(item);
        setShowModal(true);
    };

    if (loading) return <div className="p-8">読み込み中...</div>;

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-2xl font-bold mb-6">本人確認（KYC）管理</h1>

                <div className="bg-white rounded shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID / ユーザー</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">種類</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">提出日</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {items.map(item => (
                                <tr key={item.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{item.id.substring(0, 8)}...</div>
                                        <div className="text-sm text-gray-500">{item.user_name || item.user_email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.document_type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(item.submitted_at).toLocaleString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                                    ${item.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                item.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {item.status === 'approved' ? '承認済' : item.status === 'rejected' ? '否認' : '未対応'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button onClick={() => openModal(item)} className="text-blue-600 hover:text-blue-900 flex items-center">
                                            <EyeIcon className="w-4 h-4 mr-1" /> 確認
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">データがありません</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Detail Modal */}
                {showModal && selectedCase && (
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                                <h3 className="text-lg font-medium">本人確認詳細</h3>
                                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                    ✕
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div>
                                        <p className="text-sm text-gray-500">ユーザー</p>
                                        <p className="font-medium">{selectedCase.user_name}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">書類タイプ</p>
                                        <p className="font-medium">{selectedCase.document_type}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                    <div>
                                        <p className="mb-2 font-medium">表面</p>
                                        {isSuperAdmin ? (
                                            selectedCase.document_front_image ? (
                                                <img src={`${API_BASE}${selectedCase.document_front_image}`} alt="Front" className="w-full h-auto rounded border" />
                                            ) : <p className="text-gray-400">なし</p>
                                        ) : (
                                            <div className="w-full h-40 bg-gray-100 flex items-center justify-center border rounded">
                                                <p className="text-gray-500 text-sm">閲覧権限がありません</p>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <p className="mb-2 font-medium">裏面</p>
                                        {isSuperAdmin ? (
                                            selectedCase.document_back_image ? (
                                                <img src={`${API_BASE}${selectedCase.document_back_image}`} alt="Back" className="w-full h-auto rounded border" />
                                            ) : <p className="text-gray-400">なし</p>
                                        ) : (
                                            <div className="w-full h-40 bg-gray-100 flex items-center justify-center border rounded">
                                                <p className="text-gray-500 text-sm">閲覧権限がありません</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {selectedCase.status === 'pending' && (
                                    <div className="flex justify-end gap-4 border-t pt-4">
                                        <button
                                            onClick={() => handleAction(selectedCase.id, 'rejected')}
                                            disabled={processing}
                                            className="px-4 py-2 border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50">
                                            否認する
                                        </button>
                                        <button
                                            onClick={() => handleAction(selectedCase.id, 'approved')}
                                            disabled={processing}
                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                                            承認する
                                        </button>
                                    </div>
                                )}
                                {selectedCase.status !== 'pending' && (
                                    <div className="text-right text-gray-500">
                                        この申請は {selectedCase.status === 'approved' ? '承認' : '否認'} 済みです。
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AdminIdentityVerificationPage;
